import {
  Button,
  Card,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap';
import { useConvertEOIToOffplanModal } from './ConvertEOIToOffplanContext';
import React, { useCallback, useEffect, useState } from 'react';
import { Building, DollarSign, Expand } from 'lucide-react';
import { currencyFormatter } from '../../../../explorer/utils';
import { DealCategory, DealStatus, DealType, formatValue, HttpStatus } from '../../../../../utils/envConfig';
import useAxiosPrivate from '../../../../../hooks/useAxiosPrivate';
import { showToast } from '../../../../../utils/toast/toast';
import { RSelect } from '../../../../../components/Component';
import { safeParseNumber } from '../../../../../utils/Utils';
import { formatCurrency } from '../../transactions/utils';

const initialFormData = {
  propertyType: null,
  beds: null,
  baths: null,
  size: '',
  unitId: '',
  agreedPrice: '',
  agreedCommission: '',
  isMortgage: false,
  mortgageProvider: ''
};

const ConvertEOIToOffplanModal = ({ deal, refreshCallback = async () => { } }) => {
  const { isOpen, toggle } = useConvertEOIToOffplanModal();
  const axios = useAxiosPrivate();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
  const [bedsOptions, setBedsOptions] = useState([]);
  const [bathsOptions, setBathsOptions] = useState([]);

  // Initialize form data from EOI deal
  useEffect(() => {
    if (deal && isOpen) {
      // Pre-fill data from EOI deal
      setFormData({
        ...initialFormData,
        propertyType: deal?.unitDetails?.propertyType ? {
          value: deal.unitDetails.propertyType,
          label: deal.unitDetails.propertyType
        } : null,
        beds: deal?.unitDetails?.beds ? {
          value: deal.unitDetails.beds,
          label: deal.unitDetails.beds
        } : null,
        size: deal?.unitDetails?.size || '',
        agreedPrice: deal?.estimated_price ? deal.estimated_price.toString() : '',
      });

      // Fetch property types for the selected development
      fetchPropertyTypes();
    }
  }, [deal, isOpen]);

  // Fetch property types for the selected development
  const fetchPropertyTypes = async () => {
    if (!deal?.offplan_id) return;

    try {
      const { data } = await axios.get(`/off-plan/${deal.offplan_id}`);
      if (data && data.propertyTypes) {
        const propTypes = data.propertyTypes.map(pt => ({
          value: pt.propertyType,
          label: pt.propertyType,
          beds: pt.beds
        }));
        setPropertyTypeOptions(propTypes);
      }
    } catch (error) {
      console.error("Error fetching property types:", error);
    }
  };

  // Update beds options when property type changes
  useEffect(() => {
    if (formData.propertyType) {
      const selectedPropertyType = propertyTypeOptions.find(
        pt => pt.value === formData.propertyType.value
      );

      if (selectedPropertyType && selectedPropertyType.beds) {
        const bedsOpts = selectedPropertyType.beds.map(bed => ({
          value: bed.name,
          label: bed.name,
          size: bed.size
        }));
        setBedsOptions(bedsOpts);
      } else {
        setBedsOptions([
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: '3', label: '3' },
          { value: '4', label: '4' },
          { value: '5', label: '5' },
          { value: '6', label: '6' }
        ]);
      }
    }
  }, [formData.propertyType, propertyTypeOptions]);

  // Set default bathroom options
  useEffect(() => {
    setBathsOptions([
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
      { value: '5', label: '5' },
      { value: '6', label: '6' }
    ]);
  }, []);

  // Update size when beds change
  useEffect(() => {
    if (formData.beds) {
      const selectedBed = bedsOptions.find(
        bed => bed.value === formData.beds.value
      );

      if (selectedBed && selectedBed.size) {
        setFormData(prev => ({
          ...prev,
          size: selectedBed.size.toString()
        }));
      }
    }
  }, [formData.beds, bedsOptions]);

  // Validate form data
  const validateFormData = useCallback(() => {
    const newErrors = {};

    if (!formData.propertyType) {
      newErrors.propertyType = 'Property type is required';
    }

    if (!formData.beds) {
      newErrors.beds = 'Number of bedrooms is required';
    }

    if (!formData.baths) {
      newErrors.baths = 'Number of bathrooms is required';
    }

    if (!formData.size || isNaN(+formData.size) || +formData.size <= 0) {
      newErrors.size = !formData.size
        ? 'Property size is required'
        : +formData.size <= 0
          ? 'Size cannot be less than 1'
          : 'Invalid property size';
    }

    if (!formData.unitId) {
      newErrors.unitId = 'Unit ID is required';
    }

    const agreedPrice = Number(formData.agreedPrice?.replaceAll(',', ''));
    if (!agreedPrice || isNaN(agreedPrice) || agreedPrice <= 0) {
      newErrors.agreedPrice = !agreedPrice
        ? 'Agreed price is required'
        : isNaN(agreedPrice)
          ? 'Price must be a number'
          : 'Price cannot be less than 1';
    }

    const agreedCommission = Number(formData.agreedCommission);
    if (isNaN(agreedCommission) || agreedCommission > 100 || agreedCommission < 0) {
      newErrors.agreedCommission = isNaN(agreedCommission)
        ? 'Commission must be a number'
        : agreedCommission > 100
          ? 'Commission cannot be more than 100%'
          : 'Commission cannot be less than 0%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);



  // Submit form
  const submitForm = async () => {
    try {
      if (!validateFormData()) return;

      setIsLoading(true);


      // Create new primary offplan deal
      const dealData = {
        category: DealCategory.PrimaryOffplan,
        type: DealType.Offplan,
        isMortgage: formData.isMortgage,
        mortgageProvider: formData.mortgageProvider,
        agreedSalePrice: Number(formData.agreedPrice.replaceAll(',', '')),
        agreedCommission: Number(formData.agreedCommission),
        unitId: formData.unitId,
        propertyType: formData.propertyType?.value,
        beds: formData.beds?.value,
        baths: formData.baths?.value,
        size: formData.size
      };

      const { data: newDeal } = await axios.patch(`/deals/${deal.id}/convert-to-primary-offplan`, dealData);

      if (newDeal) {


        showToast('EOI deal converted to primary offplan deal successfully', 'success');
        toggle();
        refreshCallback();
      }
    } catch (err) {
      console.error("Error converting deal:", err);
      showToast(err?.response?.data?.message || 'Failed to convert deal', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };



  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader className="bg-white border-0" toggle={toggle}>
        Convert EOI Deal to Primary Offplan Deal
      </ModalHeader>
      <ModalBody className="bg-white">
        <PropertyInformation deal={deal} />

        <h5 className="mt-4">Property Details</h5>
        <Row className="py-2">
          <Col sm={6}>
            <Label htmlFor="propertyType">
              Property Type <span className="text-danger">*</span>
            </Label>
            <RSelect
              options={propertyTypeOptions}
              value={formData.propertyType}
              onChange={(value) => {
                setFormData(prev => ({
                  ...prev,
                  propertyType: value,
                  beds: null
                }));
                setErrors(prev => ({
                  ...prev,
                  propertyType: null
                }));
              }}
              className={errors?.propertyType ? 'border-danger' : ''}
            />
            {errors?.propertyType && (
              <small className="text-danger">{errors.propertyType}</small>
            )}
          </Col>

          <Col sm={6}>
            <Label htmlFor="beds">
              Bedrooms <span className="text-danger">*</span>
            </Label>
            <RSelect
              options={bedsOptions}
              value={formData.beds}
              onChange={(value) => {
                setFormData(prev => ({
                  ...prev,
                  beds: value
                }));
                setErrors(prev => ({
                  ...prev,
                  beds: null
                }));
              }}
              isDisabled={!formData.propertyType}
              className={errors?.beds ? 'border-danger' : ''}
            />
            {errors?.beds && (
              <small className="text-danger">{errors.beds}</small>
            )}
          </Col>
        </Row>

        <Row className="py-2">
          <Col sm={6}>
            <Label htmlFor="baths">
              Bathrooms <span className="text-danger">*</span>
            </Label>
            <RSelect
              options={bathsOptions}
              value={formData.baths}
              onChange={(value) => {
                setFormData(prev => ({
                  ...prev,
                  baths: value
                }));
                setErrors(prev => ({
                  ...prev,
                  baths: null
                }));
              }}
              className={errors?.baths ? 'border-danger' : ''}
            />
            {errors?.baths && (
              <small className="text-danger">{errors.baths}</small>
            )}
          </Col>

          <Col sm={6}>
            <Label htmlFor="size">
              Size (sqft) <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              id="size"
              name="size"
              value={formData.size}
              onChange={handleInputChange}
              className={errors?.size ? 'border-danger' : ''}
            />
            {errors?.size && (
              <small className="text-danger">{errors.size}</small>
            )}
          </Col>
        </Row>

        <Row className="py-2">
          <Col sm={12}>
            <Label htmlFor="unitId">
              Unit ID <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="unitId"
              name="unitId"
              value={formData.unitId}
              onChange={handleInputChange}
              className={errors?.unitId ? 'border-danger' : ''}
              placeholder="e.g. A-101"
            />
            {errors?.unitId && (
              <small className="text-danger">{errors.unitId}</small>
            )}
          </Col>
        </Row>

        <h5 className="mt-4">Financial Details</h5>
        <Row className="py-2">
          <Col sm={6}>
            <Label htmlFor="agreedPrice">
              Agreed Price (AED) <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="agreedPrice"
              name="agreedPrice"
              value={formatValue(formData.agreedPrice)}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9,]/g, '');
                setFormData(prev => ({
                  ...prev,
                  agreedPrice: value
                }));
                setErrors(prev => ({
                  ...prev,
                  agreedPrice: null
                }));
              }}
              className={errors?.agreedPrice ? 'border-danger' : ''}
            />
            {errors?.agreedPrice && (
              <small className="text-danger">{errors.agreedPrice}</small>
            )}
          </Col>

          <Col sm={6}>
            <Label htmlFor="agreedCommission">
              Commission (%) <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              id="agreedCommission"
              name="agreedCommission"
              value={formData.agreedCommission}
              onChange={handleInputChange}
              className={errors?.agreedCommission ? 'border-danger' : ''}
              min="0"
              max="100"
              step="0.01"
            />
            {errors?.agreedCommission && (
              <small className="text-danger">{errors.agreedCommission}</small>
            )}
          </Col>
        </Row>

        <Row className="py-2">
          <Col sm={6}>
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="isMortgage"
                checked={formData.isMortgage}
                onChange={() => {
                  setFormData(prev => ({
                    ...prev,
                    isMortgage: !prev.isMortgage,
                    mortgageProvider: !prev.isMortgage ? prev.mortgageProvider : ''
                  }));
                }}
              />
              <label className="custom-control-label" htmlFor="isMortgage">
                Is Mortgage?
              </label>
            </div>
          </Col>

          {formData.isMortgage && (
            <Col sm={6}>
              <Label htmlFor="mortgageProvider">
                Mortgage Provider
              </Label>
              <Input
                type="text"
                id="mortgageProvider"
                name="mortgageProvider"
                value={formData.mortgageProvider}
                onChange={handleInputChange}
                placeholder="Enter mortgage provider"
              />
            </Col>
          )}
        </Row>
      </ModalBody>
      <ModalFooter className="bg-white border-0">
        <Button color="light" onClick={toggle}>
          Cancel
        </Button>
        <Button color="primary" disabled={isLoading} onClick={submitForm}>
          {isLoading ? <Spinner size="sm" /> : 'Convert to Primary Offplan Deal'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(ConvertEOIToOffplanModal);

const PropertyInformation = ({ deal }) => {
  return (
    <Card className="border">
      <CardHeader className="bg-white p-3">
        <h5>EOI Deal Information</h5>
      </CardHeader>
      <div className="py-2 px-3">
        <Row>
          <Col sm={6}>
            <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
              <span className="fw-bold">
                <Building size={14} />
              </span>
              <span className="text-muted">
                {`${deal?.developer?.name || 'Unknown developer'}, ${deal?.offplan?.developmentName || 'Unknown project'
                  }`}
              </span>
            </div>
          </Col>
          <Col sm={6}>
            <div className="d-flex align-items-center">
              <span className="fw-bold" style={{ verticalAlign: 'middle' }}>
                <DollarSign size={14} />
              </span>
              <span className="text-muted ms-2">
                {`Estimated Price: ${deal?.estimated_price
                  ? currencyFormatter({ isDecimal: true, decimalPlaces: 2 }).format(
                    deal?.estimated_price
                  )
                  : 'Unknown'
                  }`}
              </span>
            </div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col sm={6}>
            <div className="d-flex align-items-center">
              <span className="fw-bold">Property Type:</span>
              <span className="text-muted ms-2">
                {deal?.unitDetails?.propertyType || 'Unknown'}
              </span>
            </div>
          </Col>
          <Col sm={6}>
            <div className="d-flex align-items-center">
              <span className="fw-bold">Beds:</span>
              <span className="text-muted ms-2">
                {deal?.unitDetails?.beds || 'Unknown'}
              </span>
            </div>
          </Col>
        </Row>
        {deal?.paid_amount && (
          <Row className="mt-2">
            <Col sm={12}>
              <div className="d-flex align-items-center">
                <span className="fw-bold">Paid Amount:</span>
                <span className="text-muted ms-2">
                  {currencyFormatter({ isDecimal: true, decimalPlaces: 2 }).format(deal.paid_amount)}
                </span>
              </div>
            </Col>
          </Row>
        )}
        {deal?.notes && (
          <Row className="mt-2">
            <Col sm={12}>
              <div>
                <span className="fw-bold">Notes:</span>
                <p className="text-muted mt-1">{deal.notes}</p>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </Card>
  );
};
